import React, { useEffect, useState } from 'react';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { Card, IconButton, ProfileImage, SearchBar } from '../ui';
import { employeeApi } from './employeeApi';

const ZoneEmployeeAssignment = ({ 
  zoneId,
  zoneName,
  assignedEmployees = [],
  excludedEmployees = [],
  employeeType = 'guards',
  siteId = null,
  onEmployeeAdd,
  onEmployeeRemove,
  onNoteEdit,
  isExpanded = false,
  onToggleExpanded,
  shiftValidationData = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmployeeList, setShowEmployeeList] = useState(false);
  const [employeeStatuses, setEmployeeStatuses] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [initialAssignedEmployeeIds, setInitialAssignedEmployeeIds] = useState(new Set());
  const [leaveAvailabilityCache, setLeaveAvailabilityCache] = useState({});
  const [shiftAssignmentCache, setShiftAssignmentCache] = useState({});

  // Filter out employees who are assigned to other zones
  const filterExcludedEmployees = (employees) => {
    const excludedUserIds = excludedEmployees.map(emp => emp.user_id);
    return employees.filter(employee => !excludedUserIds.includes(employee.user_id));
  };

  // Helper function to get cached leave availability or fetch if not cached
  const getLeaveAvailability = async (userId, date) => {
    const cacheKey = `${userId}_${date}`;
    
    // Check if we already have this data cached
    if (leaveAvailabilityCache[cacheKey]) {
      console.log(`🔍 Using cached leave data for ${userId} on ${date}`);
      return leaveAvailabilityCache[cacheKey];
    }

    try {
      console.log(`🔍 Fetching leave data for ${userId} on ${date}`);
      const leaveAvailability = await employeeApi.checkLeaveAvailability({
        userId: userId,
        date: date
      });
      
      // Cache the result
      setLeaveAvailabilityCache(prev => ({
        ...prev,
        [cacheKey]: leaveAvailability
      }));
      
      return leaveAvailability;
    } catch (error) {
      console.error(`Failed to check leave status for employee ${userId} after retries:`, error);
      // Don't cache error results - allow retry on next call
      // Return a default available status to prevent blocking the UI
      return { 
        available: true,
        leave_type: null,
        leave_reason: null,
        start_date: null,
        end_date: null
      };
    }
  };

  // Helper function to get cached shift assignment status or fetch if not cached
  const getShiftAssignmentStatus = async (userId, date, startTime, endTime) => {
    const cacheKey = `${userId}_${date}_${startTime}_${endTime}`;
    
    // Check if we already have this data cached
    if (shiftAssignmentCache[cacheKey] !== undefined) {
      console.log(`🔍 Using cached shift assignment data for ${userId} on ${date} (${startTime} - ${endTime})`);
      return shiftAssignmentCache[cacheKey];
    }

    try {
      console.log(`🔍 Fetching shift assignment data for ${userId} on ${date} (${startTime} - ${endTime})`);
      const isAssigned = await employeeApi.isUserAssignedToShift({
        userId: userId,
        date: date,
        startTime: startTime,
        endTime: endTime
      });
      
      // Cache the result
      setShiftAssignmentCache(prev => ({
        ...prev,
        [cacheKey]: isAssigned
      }));
      
      return isAssigned;
    } catch (error) {
      console.warn(`Could not check shift assignment for employee ${userId}:`, error);
      // Don't cache error results - allow retry on next call
      // Return false on error to allow assignment to proceed
      return false;
    }
  };

  // Fetch employees when component mounts or when needed
  useEffect(() => {
    if (isExpanded && allEmployees.length === 0) {
      fetchEmployees();
    } else if (isExpanded && allEmployees.length > 0) {
      // If employees are already loaded, force validation when expanding
      console.log('🔍 Component expanded - forcing validation');
      validateBasicEmployeeAvailability();
    }
  }, [isExpanded]);

  // Force re-validation when excluded employees change significantly
  useEffect(() => {
    if (isExpanded && allEmployees.length > 0 && excludedEmployees.length > 0) {
      console.log('🔍 Excluded employees changed, forcing re-validation');
      validateBasicEmployeeAvailability();
    }
  }, [excludedEmployees]);

  // Force validation when employee list is shown
  useEffect(() => {
    if (showEmployeeList && allEmployees.length > 0) {
      console.log('🔍 Employee list shown - forcing validation');
      validateBasicEmployeeAvailability();
    }
  }, [showEmployeeList, allEmployees]);

  // Update filtered employees when excluded employees change
  useEffect(() => {
    if (allEmployees.length > 0) {
      // Don't filter out employees, just show all with proper status
      setFilteredEmployees(allEmployees);
    }
  }, [excludedEmployees, allEmployees]);

  // Set initial assigned employee IDs when assignedEmployees change
  useEffect(() => {
    const initialIds = new Set(assignedEmployees.map(emp => 
      typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id)
    ));
    setInitialAssignedEmployeeIds(initialIds);
  }, [assignedEmployees]);

  // Validate employee availability when employees are loaded and dependencies change
  useEffect(() => {
    if (isExpanded && allEmployees.length > 0) {
      console.log('🔍 Validation trigger - isExpanded:', isExpanded, 'allEmployees:', allEmployees.length, 'excludedEmployees:', excludedEmployees.length);
      
      // Run validation immediately (loading state is handled inside validation functions)
      if (shiftValidationData && shiftValidationData.date) {
        console.log('🔍 Running full validation with shift data');
        validateEmployeeAvailability();
      } else {
        console.log('🔍 Running basic validation without shift data');
        validateBasicEmployeeAvailability();
      }
    }
  }, [isExpanded, allEmployees, shiftValidationData, initialAssignedEmployeeIds]);

  // Handle zone assignment changes (but not leave status or shift assignment) when excludedEmployees change
  useEffect(() => {
    if (isExpanded && allEmployees.length > 0 && excludedEmployees.length > 0) {
      console.log('🔍 Excluded employees changed - updating zone assignment status only');
      // Only update zone assignment status, preserve leave status and shift assignment status (which are cached)
      const statuses = { ...employeeStatuses };
      
      allEmployees.forEach((employee) => {
        const userId = employee.user_id || employee.employee_id;
        if (!userId) return;

        const isAssignedToThisZone = initialAssignedEmployeeIds.has(userId);
        const assignedToOtherZone = excludedEmployees.find(emp => {
          const empUserId = typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id);
          return empUserId === userId;
        });

        // Only update zone-related status, preserve leave status and shift assignment status
        if (statuses[userId]) {
          const existingStatus = statuses[userId];
          let status = 'available';
          let statusMessage = 'Available';
          let isAvailable = true;

          // Priority order: assigned_to_this_zone > assigned_to_other_zone > on_leave > assigned_to_shift > available
          if (isAssignedToThisZone) {
            status = 'assigned_to_this_zone';
            statusMessage = 'Already assigned to this zone';
            isAvailable = false;
          } else if (assignedToOtherZone) {
            status = 'assigned_to_other_zone';
            statusMessage = `Assigned to ${assignedToOtherZone.assignedToZoneName || 'another zone'}`;
            isAvailable = false;
          } else if (existingStatus.isOnLeave) {
            status = 'on_leave';
            statusMessage = 'On leave';
            isAvailable = false;
          } else if (existingStatus.isAssignedToShift) {
            status = 'assigned_to_shift';
            statusMessage = 'Assigned to another shift';
            isAvailable = false;
          }

          statuses[userId] = {
            ...existingStatus,
            isAssignedToThisZone,
            isAssignedToOtherZone: !!assignedToOtherZone,
            assignedToZoneName: assignedToOtherZone?.assignedToZoneName,
            status,
            statusMessage,
            isAvailable
          };
        }
      });
      
      setEmployeeStatuses(statuses);
    }
  }, [excludedEmployees]);

  // Fetch employees from API
  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let employeesData = [];
      
      if (employeeType === 'guards') {
        employeesData = await employeeApi.fetchAvailableGuards();
      } else if (employeeType === 'supervisors') {
        employeesData = await employeeApi.fetchAvailableSupervisors();
      } else {
        // Fallback to all users
        employeesData = await employeeApi.getAllUsers();
      }

      // Process employees data to include profile photos
      const processedEmployees = await Promise.all(
        employeesData.map(async (employee) => {
          try {
            const imagePath = await employeeApi.getProfilePhoto(employee.user_id);
            
            // Cache the profile image if we have an image path
            // This ensures ProfileImage components can use cached URLs immediately
            if (imagePath && imagePath.trim() !== '') {
              const userId = employee.user_id || employee.employee_id;
              
              // Check if already cached
              const cachedUrl = profileImageCache.get(userId);
              if (!cachedUrl) {
                // If it's already a URL, cache it directly
                if (imagePath.startsWith('http')) {
                  profileImageCache.set(userId, imagePath);
                } else {
                  // It's an S3 path, cache the path and fetch presigned URL in background
                  profileImageCache.setS3Path(userId, imagePath);
                  // Fetch presigned URL in background (don't await to avoid blocking)
                  userService.getS3PresignedUrl(imagePath)
                    .then(presignedUrl => {
                      if (presignedUrl) {
                        profileImageCache.set(userId, presignedUrl, imagePath);
                        console.log(`💾 Cached profile image URL for user ${userId}`);
                      }
                    })
                    .catch(error => {
                      console.warn(`⚠️ Failed to cache presigned URL for user ${userId}:`, error);
                    });
                }
              }
            }
            
            return {
              ...employee,
              image_path: imagePath,
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || employeeType,
              employee_id: employee.employee_id || employee.user_id
            };
          } catch (photoError) {
            console.warn(`Failed to fetch profile photo for ${employee.user_id}:`, photoError);
            return {
              ...employee,
              image_path: '',
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || employeeType,
              employee_id: employee.employee_id || employee.user_id
            };
          }
        })
      );

      setAllEmployees(processedEmployees);
      // Show all employees (don't filter)
      setFilteredEmployees(processedEmployees);
      
      // Validate immediately after employees are loaded (keep loading until validation completes)
      console.log('🔍 Employees fetched - starting validation');
      await validateBasicEmployeeAvailability();
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError(error.message || 'Failed to fetch employees');
      setAllEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Utility function to check for time conflicts between shifts
  const checkTimeConflict = (startTime1, endTime1, startTime2, endTime2) => {
    // Convert time strings to minutes for easier comparison
    const timeToMinutes = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
      if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
      return totalMinutes;
    };

    const start1 = timeToMinutes(startTime1);
    const end1 = timeToMinutes(endTime1);
    const start2 = timeToMinutes(startTime2);
    const end2 = timeToMinutes(endTime2);

    // Check for overlap: one shift starts before the other ends
    return (start1 < end2 && start2 < end1);
  };

  // Basic validation without shift data - just check for zone assignments and leave status
  const validateBasicEmployeeAvailability = async () => {
    console.log('🔍 Starting basic employee validation for zone assignments');
    console.log('🔍 allEmployees:', allEmployees.length, 'excludedEmployees:', excludedEmployees.length, 'initialAssignedEmployeeIds:', Array.from(initialAssignedEmployeeIds));
    
    setIsValidating(true);
    
    try {
      const statuses = {};

      // Process employees in parallel to check leave status and overlapping shifts
      const validationPromises = allEmployees.map(async (employee) => {
        const userId = employee.user_id || employee.employee_id;
        if (!userId) {
          console.warn('Employee without user_id:', employee);
          return { userId, status: 'error' };
        }

        // Check if employee is already assigned to this specific zone
        const isAssignedToThisZone = initialAssignedEmployeeIds.has(userId);
        
        // Check if employee is assigned to any other zone
        const assignedToOtherZone = excludedEmployees.find(emp => {
          const empUserId = typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id);
          return empUserId === userId;
        });

        console.log(`🔍 Employee ${employee.display_name} (${userId}): isAssignedToThisZone=${isAssignedToThisZone}, assignedToOtherZone=${!!assignedToOtherZone}`);

        // Check if employee is on leave (if we have shift validation data with date)
        let isOnLeave = false;
        let leaveAvailability = null;
        if (shiftValidationData && shiftValidationData.date) {
          leaveAvailability = await getLeaveAvailability(userId, shiftValidationData.date);
          isOnLeave = !leaveAvailability.available;
          console.log(`🔍 Employee ${employee.display_name} (${userId}): isOnLeave=${isOnLeave}`);
        }

        // Check if employee is assigned to another overlapping shift (using cache)
        let isAssignedToShift = false;
        if (shiftValidationData && shiftValidationData.date && shiftValidationData.startTime && shiftValidationData.endTime) {
          isAssignedToShift = await getShiftAssignmentStatus(
            userId,
            shiftValidationData.date,
            shiftValidationData.startTime,
            shiftValidationData.endTime
          );
          console.log(`🔍 Employee ${employee.display_name} (${userId}): isAssignedToShift=${isAssignedToShift}`);
        }

        // Determine status with priority order (preserving zone assignment logic)
        let status = 'available';
        let statusMessage = 'Available';
        let isAvailable = true;

        if (isAssignedToThisZone) {
          status = 'assigned_to_this_zone';
          statusMessage = 'Already assigned to this zone';
          isAvailable = false;
        } else if (assignedToOtherZone) {
          status = 'assigned_to_other_zone';
          statusMessage = `Assigned to ${assignedToOtherZone.assignedToZoneName || 'another zone'}`;
          isAvailable = false;
        } else if (isOnLeave) {
          status = 'on_leave';
          statusMessage = 'On leave';
          isAvailable = false;
        } else if (isAssignedToShift) {
          status = 'assigned_to_shift';
          statusMessage = 'Assigned to another shift';
          isAvailable = false;
        }

        return {
          userId,
          status,
          statusMessage,
          isAvailable,
          isAssignedToThisZone,
          isAssignedToOtherZone: !!assignedToOtherZone,
          assignedToZoneName: assignedToOtherZone?.assignedToZoneName,
          isOnLeave,
          isAssignedToShift,
          leaveDetails: leaveAvailability
        };
      });

      // Wait for all validations to complete
      const results = await Promise.all(validationPromises);
      
      // Convert results to statuses object
      results.forEach(result => {
        if (result.userId && result.status !== 'error') {
          statuses[result.userId] = result;
        }
      });

      console.log('🔍 Final validation statuses:', statuses);
      setEmployeeStatuses(statuses);
      setIsValidating(false);
    } catch (error) {
      console.error('Error validating employee availability:', error);
      setIsValidating(false);
    }
  };

  // Validate employee availability for the current shift
  // This implements the complete Flutter validation logic from the provided guide
  const validateEmployeeAvailability = async () => {
    if (!shiftValidationData || !shiftValidationData.date) {
      console.log('No shift validation data available');
      return;
    }

    console.log('🔍 Starting zone employee validation with initial assigned IDs:', Array.from(initialAssignedEmployeeIds));
    setIsValidating(true);
    const statuses = {};

    try {
      // Get current shift's zone assignments for conflict checking
      const currentShiftZoneGuards = shiftValidationData.currentShift?.zoneGuards || {};
      const currentShiftGuards = Object.values(currentShiftZoneGuards).flat();
      const currentShiftGuardIds = currentShiftGuards.map(guard => 
        typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id)
      );

      // Validate each employee's availability
      const validationPromises = allEmployees.map(async (employee) => {
        const userId = employee.user_id || employee.employee_id;
        if (!userId) return;

         try {
           // Step 1: Check if employee is already assigned to this specific zone
           const isAssignedToThisZone = initialAssignedEmployeeIds.has(userId);
           console.log(`🔍 Zone Employee ${employee.display_name} (${userId}): isAssignedToThisZone = ${isAssignedToThisZone}`);

           // Step 2: Check if employee is assigned to any other zone
           let isAssignedToOtherZone = false;
           if (currentShiftZoneGuards && Object.keys(currentShiftZoneGuards).length > 0) {
             for (const [otherZoneId, guards] of Object.entries(currentShiftZoneGuards)) {
               if (otherZoneId !== zoneId) { // Different zone than current
                 const isInOtherZone = guards.some(guard => 
                   (typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id)) === userId
                 );
                 if (isInOtherZone) {
                   isAssignedToOtherZone = true;
                   break;
                 }
               }
             }
           }
           console.log(`🔍 Zone Employee ${employee.display_name} (${userId}): isAssignedToOtherZone = ${isAssignedToOtherZone}`);

           // Step 3: Check if employee is already assigned to this shift (as supervisor)
           const isAlreadyAssignedToThisShift = false; // For zone assignments, this is handled by zone-specific logic

          // Step 4: Check if employee is assigned to another overlapping shift (using cache)
          let isAssignedToShift = false;
          isAssignedToShift = await getShiftAssignmentStatus(
            userId,
            shiftValidationData.date,
            shiftValidationData.startTime,
            shiftValidationData.endTime
          );
          console.log(`🔍 Zone Employee ${employee.display_name} (${userId}): isAssignedToShift = ${isAssignedToShift}`);

          // Step 5: Check if employee is on leave
          const leaveAvailability = await getLeaveAvailability(userId, shiftValidationData.date);
          const isOnLeave = !leaveAvailability.available;
          console.log(`🔍 Zone Employee ${employee.display_name} (${userId}): isOnLeave = ${isOnLeave}`);

           // Step 6: Determine status following the exact priority from the guide
           // Priority order: assigned_to_this_zone > assigned_to_other_zone > on_leave > assigned_to_shift > available
           let status = 'available';
           let statusMessage = 'Available';
           let isAvailable = true;

           if (isAssignedToThisZone) {
             status = 'assigned_to_this_zone';
             statusMessage = 'Already assigned to this zone';
             isAvailable = false;
           } else if (isAssignedToOtherZone) {
             status = 'assigned_to_other_zone';
             statusMessage = 'Assigned to another zone';
             isAvailable = false;
           } else if (isOnLeave) {
             status = 'on_leave';
             statusMessage = 'On leave';
             isAvailable = false;
           } else if (isAssignedToShift) {
             status = 'assigned_to_shift';
             statusMessage = 'Assigned to another shift';
             isAvailable = false;
           }

          console.log(`🔍 Final zone status for ${employee.display_name}: ${status} - ${statusMessage}`);

          statuses[userId] = {
            status,
            statusMessage,
            isAvailable,
            leaveDetails: leaveAvailability,
            isAssignedToThisZone,
            isAssignedToOtherZone,
            isAssignedToShift,
            isOnLeave
          };
        } catch (error) {
          console.error(`Error validating employee ${userId}:`, error);
          // Default to available on error
          statuses[userId] = {
            status: 'available',
            statusMessage: 'Available',
            isAvailable: true,
            leaveDetails: null,
            isAssignedToThisZone: false,
            isAssignedToOtherZone: false,
            isAssignedToShift: false,
            isOnLeave: false
          };
        }
      });

      await Promise.all(validationPromises);
      setEmployeeStatuses(statuses);
    } catch (error) {
      console.error('Error validating employee availability:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Employee search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0) {
      // Filter employees based on search term
      const searchFiltered = allEmployees.filter(employee => {
        const searchTerm = value.toLowerCase();
        return (
          employee.display_name?.toLowerCase().includes(searchTerm) ||
          employee.role?.toLowerCase().includes(searchTerm) ||
          employee.employee_id?.toLowerCase().includes(searchTerm) ||
          employee.user_id?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredEmployees(searchFiltered);
      setSearchSuggestions(searchFiltered.map(emp => emp.display_name));
    } else {
      // Show all employees when search is empty
      setFilteredEmployees(allEmployees);
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // Find the employee by display name and show only that employee
    const selectedEmployee = allEmployees.find(emp => emp.display_name === suggestion);
    if (selectedEmployee) {
      setFilteredEmployees([selectedEmployee]);
    }
  };

  const handleAddEmployee = (employee) => {
    const isAlreadyAssigned = assignedEmployees.find(emp => emp.user_id === employee.user_id);
    
    // Check if employee is assigned to other zones
    const isAssignedToOtherZone = excludedEmployees.find(emp => emp.user_id === employee.user_id);
    
    if (isAssignedToOtherZone) {
      const zoneName = isAssignedToOtherZone.assignedToZoneName || 'another zone';
      console.warn(`Employee ${employee.display_name} is already assigned to ${zoneName}`);
      alert(`This employee is already assigned to ${zoneName} and cannot be assigned to multiple zones.`);
      return;
    }
    
    if (!isAlreadyAssigned) {
      onEmployeeAdd(zoneId, employee);
    }
    
    setSearchTerm('');
    setSearchSuggestions([]);
    // Reset to show all employees
    setFilteredEmployees(allEmployees);
  };

  const handleRemoveEmployee = (employee) => {
    onEmployeeRemove(zoneId, employee);
  };

  const handleToggleEmployeeList = () => {
    const newShowEmployeeList = !showEmployeeList;
    setShowEmployeeList(newShowEmployeeList);
    
    if (newShowEmployeeList) {
      // Opening the employee list
      if (allEmployees.length === 0) {
        fetchEmployees();
      } else {
        // Show all employees when displaying the list
        setFilteredEmployees(allEmployees);
        // Force validation when opening the list
        setTimeout(async () => {
          console.log('🔍 Toggle button clicked - forcing validation');
          await validateBasicEmployeeAvailability();
        }, 100);
      }
    }
  };

  const handleNoteEdit = (employee, newNote) => {
    onNoteEdit(zoneId, employee, newNote);
  };

  return (
    <div className="w-full">
      {/* Zone Header */}
      <Card.Gradient 
        className={`p-4 transition-all duration-200 ${
          isExpanded ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              {zoneName}
            </h4>
            
            {/* Assigned employees display */}
            <div className="space-y-2">
              {assignedEmployees.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 mb-2">
                    {assignedEmployees.length} employees assigned:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {assignedEmployees.map((employee, index) => (
                      <div
                        key={employee.user_id || index}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        <ProfileImage
                          userId={employee.user_id}
                          userName={employee.display_name}
                          imagePath={employee.image_path}
                          size="sm"
                          showLoading={false}
                          className="w-4 h-4"
                        />
                        <span className="truncate max-w-20">
                          {employee.display_name || 'Unknown'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveEmployee(employee);
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  No employees assigned to this zone
                </div>
              )}
            </div>
          </div>
          
          {/* Toggle Button */}
          <div className="ml-4 flex-shrink-0">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleToggleEmployeeList();
              }}
              icon={
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className={`transform transition-transform ${showEmployeeList ? 'rotate-45' : ''}`}
                >
                  <path 
                    d="M12 5V19M5 12H19" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              }
              className="bg-blue-500 hover:bg-blue-600 text-white"
            />
          </div>
        </div>
      </Card.Gradient>

      {/* Employee Assignment Section */}
      {showEmployeeList && (
        <div className="mt-4 space-y-4">
          {/* Search Bar */}
          <div className="mb-4">
            <SearchBar
              placeholder="Search for employees..."
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearchSubmit}
              suggestions={searchSuggestions}
              onSuggestionClick={handleSuggestionClick}
              className="w-full" 
            />
          </div>

          {/* Employee List */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {isLoading || isValidating ? (
              <div className="text-center text-gray-500 py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                {isLoading ? 'Loading employees...' : 'Checking employee availability...'}
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">
                <div className="mb-2">⚠️</div>
                <div className="text-sm">{error}</div>
                <button 
                  onClick={fetchEmployees}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              </div>
            ) : filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee, index) => {
                const isAssignedToZone = assignedEmployees.find(emp => emp.user_id === employee.user_id);
                const userId = employee.user_id || employee.employee_id;
                const employeeStatus = employeeStatuses[userId] || { status: 'available', statusMessage: 'Available', isAvailable: true };
                const isDisabled = !employeeStatus.isAvailable;
                
                return (
                  <div key={employee.user_id || index}>
                    {isAssignedToZone ? (
                      <Card.Gradient 
                        className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-lg ${isDisabled ? 'opacity-60' : ''}`}
                        onClick={() => !isDisabled && handleRemoveEmployee(employee)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
                            <ProfileImage
                              userId={employee.user_id}
                              userName={employee.display_name}
                              imagePath={employee.image_path}
                              size="md"
                              showLoading={false}
                            />
                            <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                              <div className="w-full text-black text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                                {employee.display_name}
                              </div>
                              <div className="w-full text-black text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                                {employee.role} • ID: {employee.employee_id}
                              </div>
                               {/* Status Message */}
                               <div className={`text-xs font-medium ${
                                 employeeStatus.status === 'assigned_to_this_zone' ? 'text-orange-600' :
                                 employeeStatus.status === 'assigned_to_other_zone' ? 'text-purple-600' :
                                 employeeStatus.status === 'already_assigned' ? 'text-blue-600' :
                                 employeeStatus.status === 'on_leave' ? 'text-orange-600' :
                                 employeeStatus.status === 'assigned_to_shift' ? 'text-red-600' :
                                 employeeStatus.status === 'local_conflict' ? 'text-red-500' :
                                 'text-green-600'
                               }`}>
                                 {employeeStatus.statusMessage}
                               </div>
                            </div>
                          </div>
                          <div className="w-6 h-6 relative flex-shrink-0 ml-3">
                            <div className="w-6 h-6 left-0 top-0 absolute bg-green-700 rounded-sm flex items-center justify-center">
                              <svg 
                                className="w-4 h-4 text-white"
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  strokeWidth={2} 
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </Card.Gradient>
                    ) : (
                      <Card.Dark 
                        className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-lg ${isDisabled ? 'opacity-60' : ''}`}
                        onClick={() => !isDisabled && handleAddEmployee(employee)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
                            <ProfileImage
                              userId={employee.user_id}
                              userName={employee.display_name}
                              imagePath={employee.image_path}
                              size="md"
                              showLoading={false}
                            />
                            <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                              <div className="w-full text-white text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                                {employee.display_name}
                              </div>
                              <div className="w-full text-gray-300 text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                                {employee.role} • ID: {employee.employee_id}
                              </div>
                               {/* Status Message */}
                               <div className={`text-xs font-medium ${
                                 employeeStatus.status === 'assigned_to_this_zone' ? 'text-orange-400' :
                                 employeeStatus.status === 'assigned_to_other_zone' ? 'text-purple-400' :
                                 employeeStatus.status === 'already_assigned' ? 'text-blue-400' :
                                 employeeStatus.status === 'on_leave' ? 'text-orange-400' :
                                 employeeStatus.status === 'assigned_to_shift' ? 'text-red-400' :
                                 employeeStatus.status === 'local_conflict' ? 'text-red-300' :
                                 'text-green-400'
                               }`}>
                                 {employeeStatus.statusMessage}
                               </div>
                            </div>
                          </div>
                          <div className="w-6 h-6 relative flex-shrink-0 ml-3">
                            <div className={`w-6 h-6 left-0 top-0 absolute rounded-sm ${
                              isDisabled 
                                ? 'bg-gray-400 border-[1.60px] border-gray-500' 
                                : 'bg-zinc-100 border-[1.60px] border-black/30'
                            }`} />
                          </div>
                        </div>
                      </Card.Dark>
                    )}
                  </div>
                );
              })
            ) : searchTerm.length > 0 ? (
              <div className="text-center text-gray-500 py-8">
                No employees found matching "{searchTerm}"
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No employees available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneEmployeeAssignment;
